import React from 'react'
import { useRouter } from 'next/router'
import xss from 'xss'

import { Spinner } from '@wartek-id/spinner'
import { Text } from '@wartek-id/text-v1'

import {
  CPKvKeys,
  useGetCPKeyPairValue,
} from 'api/content-platform/useAPIKeyValue'
import { useUserGroupSjt } from 'utils/hooks/useUserGroup'
import LoadingState from 'app/PemetaanKompetensiPage/components/LoadingState'
import {
  useApiUserDetailProfile,
  useMutateApiUserDataProfile,
} from 'api/competency-mapping/useAPIUserDetail'
import { useSETrackEvent } from '../hooks/useSETrackEvent'
import ForbiddenPage from '../components/ForbiddenPage'
import PactButton from '../components/PactButton'
import PactCheckbox from '../components/PactCheckbox'
import PeriodGuard from '../components/PeriodGuard'
import PageBreadcrumb from '../components/PageBreadcrumb'

import type {
  IUjikomPactContent,
  IUjikomPeriodData,
} from 'types/content-platform'

const fieldsMappingLabel = {
  name: 'Nama',
  nip: 'NIP',
  educationUnitName: 'Satuan Pendidikan',
}

const assesmentType = 'SJT'

const IntegrityPact = ({ currentDateTimeServer }) => {
  const { trackButtonClickIntegrityPact } = useSETrackEvent()

  const router = useRouter()

  const { hasAccess: hasNecessaryRole, isLoading: isLoadingNecessaryRole } =
    useUserGroupSjt()
  const isUjikomWhitelisted = !isLoadingNecessaryRole && hasNecessaryRole
  const [checkMark, setCheckMark] = React.useState(false)

  const { isLoading: isLoadingPactData, data: ujikomPactData } =
    useGetCPKeyPairValue<IUjikomPactContent>(
      CPKvKeys.UjikomPactContent,
      isUjikomWhitelisted
    )

  const configPemKomPeriod = useGetCPKeyPairValue<IUjikomPeriodData>(
    CPKvKeys.UjikomPeriodContent,
    isUjikomWhitelisted
  )

  const {
    data: profileData,
    isLoading: isLoadingProfile,
    isSuccess: isSuccessProfile,
  } = useApiUserDetailProfile(assesmentType, { enabled: isUjikomWhitelisted })

  const mutationUserData = useMutateApiUserDataProfile('SJT', 'create')

  const HandlePactAccept = (integrityPact: boolean) => {
    if (!integrityPact) {
      const payload = {
        user_confirmation: {
          integrityPact: true,
          integrityPactTimestamp: new Date().toISOString(),
        },
      }
      mutationUserData.mutate(payload, {
        onSuccess: () => {
          trackButtonClickIntegrityPact({
            pemKomPeriod:
              !configPemKomPeriod.isLoading && configPemKomPeriod.isSuccess
                ? configPemKomPeriod?.data?.data
                : null,
          })
          router.push('/pemetaan-kompetensi/panduan')
        },
      })
    } else {
      trackButtonClickIntegrityPact({
        pemKomPeriod:
          !configPemKomPeriod.isLoading && configPemKomPeriod.isSuccess
            ? configPemKomPeriod?.data?.data
            : null,
      })
      router.push('/pemetaan-kompetensi/panduan')
    }
  }

  React.useEffect(() => {
    if (profileData)
      setCheckMark(profileData.data?.userConfirmation?.integrityPact)
  }, [profileData])

  // To be displayed only if the role is being validated
  if (isLoadingNecessaryRole)
    return <LoadingState title="Halaman sedang dimuat..." />

  if (!isUjikomWhitelisted) return <ForbiddenPage />

  if (isLoadingProfile || isLoadingPactData)
    return (
      <div className="w-full flex justify-center">
        <Spinner />
      </div>
    )

  const RenderUserData = () => (
    <>
      {!!profileData &&
        isSuccessProfile &&
        Object.entries(fieldsMappingLabel).map((thisEntry, thisKey) => (
          <li key={`dat_${thisKey}`}>
            <Text as="p" className="inline-block align-top">
              {thisEntry[1]}:&nbsp;
              <Text variant="body-bold" as="b">
                {profileData.data[thisEntry[0]]}
              </Text>
            </Text>
          </li>
        ))}
    </>
  )

  return (
    <div className="relative flex flex-col h-max max-w-ujikom mx-auto bg-gray-10">
      <PeriodGuard
        fullPageLoading
        currentDateTimeServer={currentDateTimeServer}
      >
        <PageBreadcrumb currentPageIndex={1} className="!mb-2 mt-10" />
        <div className="bg-white m-4 p-4 rounded-md ">
          <Text>{ujikomPactData.data.intro}</Text>
          <ol className="my-6 list-disc ml-6">
            <RenderUserData />
          </ol>
          <div
            className="text-left text-[15px] mt-2 ujikom-data-tidak-sesuai-wysiwyg"
            dangerouslySetInnerHTML={{
              __html: xss(ujikomPactData.data.statement),
            }}
          ></div>
          <PactCheckbox
            checkCopy={ujikomPactData.data.checkCopy}
            checkMark={checkMark}
            setCheckMark={setCheckMark}
          />
        </div>
        <PactButton
          ctaCopy={ujikomPactData.data.ctaCopy}
          checkMark={checkMark}
          integrityPact={profileData.data?.userConfirmation?.integrityPact}
          HandlePactAccept={HandlePactAccept}
        />
      </PeriodGuard>
    </div>
  )
}

export default IntegrityPact
